import type { CSSProperties, FC, PropsWithChildren, RefObject, SyntheticEvent } from 'react';
import { useLayoutEffect, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import type { SortInput } from '@zen/types';
import { SortingOrder } from '@zen/types';
import useTracking from '@zen/utils/hooks/useTracking';
import { createTrackingEvent } from '@zen/utils/tracking';
import type { Optional } from '@zen/utils/typescript';

import { getElementWidth } from '../../helpers';
import type { TableHeaderProps } from '../../types';
import { TableConfigurationTrackingAction, TableConfigurationTrackingCategory } from '../../types';
import StyledTableHeaderCell from './StyledTableHeaderCell';
import type { DragAndDropItem, DragData, DropData } from './types';
import { COLUMN } from './types';
import useTableHeaderStyle from './useHeaderStyle';

export interface Props {
  className?: string;
  columnKey: string;
  draggable?: boolean;
  fixed?: 'left' | 'right';
  fixedOffset?: number;
  header?: TableHeaderProps;
  headerOffset?: number;
  loading?: boolean;
  offsetElementRef: RefObject<HTMLDivElement | null>;
  onColumnDragEnd?: (options: { draggedColumnKey: Optional<string> }) => void;
  onColumnDraggedOver?: (options: {
    draggedColumnKey: string;
    targetCellElement: HTMLTableCellElement | undefined;
    targetColumnKey: string;
  }) => void;
  onColumnDropped?: (options: { draggedColumnKey: string; targetColumnKey: string }) => void;
  onOrderChange?: (order: SortInput) => void;
  onResizeEnd?: (width: number) => void;
  order?: SortInput;
  resizable?: boolean;
  rightAligned: boolean;
  sortKey: string;
  sortable?: boolean;
  style?: object;
  tableId: string;
  title?: string;
  width?: string | number;
}

const TableHeaderCell: FC<PropsWithChildren<Props>> = (props) => {
  const {
    rightAligned = false,
    children,
    className,
    columnKey,
    draggable,
    headerOffset = 0,
    fixed,
    fixedOffset = 0,
    header,
    onColumnDraggedOver,
    onColumnDropped,
    onColumnDragEnd,
    onOrderChange,
    onResizeEnd,
    offsetElementRef,
    order,
    resizable,
    sortable,
    sortKey,
    style,
    tableId,
    title,
    width,
    loading = false,
    ...rest
  } = props;

  const { trackEvent } = useTracking();
  const [resizableWidth, setResizableWidth] = useState<number>();
  const isSorted: boolean = order?.field === sortKey;
  const isSortLoading: boolean = Boolean(sortable && isSorted && loading);
  const direction: SortingOrder | undefined = isSorted ? order?.direction : undefined;

  const [{ isDragging }, setDragRef, setPreviewRef] = useDrag<DragAndDropItem, undefined, DragData>({
    type: COLUMN,
    item: { id: columnKey },
    end: (item) => {
      if (!draggable) {
        return;
      }
      onColumnDragEnd?.({ draggedColumnKey: item?.id });
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    })
  });

  const [{ isOver }, setDropRef] = useDrop<DragAndDropItem, void, DropData>({
    accept: COLUMN,
    canDrop: () => {
      return !fixed; // fixed columns can't be dropped on
    },
    drop: (item) => {
      if (!draggable) {
        return;
      }
      onColumnDropped?.({ targetColumnKey: columnKey, draggedColumnKey: item.id });
    },
    hover: (item) => {
      if (!draggable) {
        return;
      }

      onColumnDraggedOver?.({
        targetColumnKey: columnKey,
        draggedColumnKey: item.id,
        targetCellElement: cellEl
      });
    },
    collect: (monitor) => ({
      isOver: monitor.canDrop() && monitor.isOver()
    })
  });

  const [cellEl, setCellEl] = useState<HTMLTableHeaderCellElement>();

  const setCellRef = (elem: HTMLTableHeaderCellElement): void => {
    setCellEl(elem);
    setDropRef(elem);
    setPreviewRef(elem);
  };

  const tableHeaderStyle: CSSProperties = useTableHeaderStyle({
    referenceElement: offsetElementRef?.current,
    topOffsetInRem: headerOffset
  });
  const cellZIndex: number = fixed ? 4 : 3;

  useLayoutEffect(() => {
    // render the cell but do not paint, get the actual width,
    // save it and rerender with a resizeable version
    const renderedWidth: number = getElementWidth(cellEl);

    setResizableWidth(renderedWidth);
  }, [cellEl, width, loading]);

  const handleHeaderClick = (event: SyntheticEvent): void => {
    if (!sortable) {
      return;
    }

    const newDirection: SortingOrder = isSorted && order?.direction === SortingOrder.ASC ? SortingOrder.DESC : SortingOrder.ASC;
    const trackingEvent = createTrackingEvent('Design System/Table/Header', 'sort', event);

    trackEvent(trackingEvent);

    trackEvent({
      category: TableConfigurationTrackingCategory,
      action: TableConfigurationTrackingAction.SORT,
      label: tableId,
      properties: {
        tableId,
        columnName: sortKey,
        sortDirection: newDirection
      }
    });

    onOrderChange?.({ field: sortKey, direction: newDirection });
  };

  return (
    <StyledTableHeaderCell
      className={className}
      columnKey={columnKey}
      data-component="table-header-cell"
      draggable={draggable}
      fixed={fixed}
      fixedOffset={fixedOffset}
      header={header}
      isDragging={isDragging}
      isOver={isOver}
      isSorted={isSorted}
      loading={isSortLoading}
      offsetElementRef={offsetElementRef}
      onClick={handleHeaderClick}
      onResizeEnd={onResizeEnd}
      resizable={resizable}
      resizableWidth={resizableWidth}
      rightAligned={rightAligned}
      setCellRef={setCellRef}
      setDragRef={setDragRef}
      setResizableWidth={setResizableWidth}
      sortable={sortable}
      sortDirection={direction}
      style={{ ...style, ...tableHeaderStyle, zIndex: cellZIndex, width, minWidth: width }}
      title={children}
      {...rest}
    />
  );
};

export default TableHeaderCell;
